import { calculateHeaderGroupHeight } from '@theme/critical';
import { Component } from '@theme/component';
import { onDocumentLoaded, changeMetaThemeColor, throttle } from '@theme/utilities';

/**
 * @typedef {Object} HeaderComponentRefs
 * @property {HTMLDivElement} headerDrawerContainer - The header drawer container element
 * @property {HTMLElement} headerMenu - The header menu element
 * @property {HTMLElement} headerRowTop - The header top row element
 */

/**
 * @typedef {CustomEvent<{ minimumReached: boolean }>} OverflowMinimumEvent
 */

/**
 * A custom element that manages the site header.
 *
 * @extends {Component<HeaderComponentRefs>}
 */

class HeaderComponent extends Component {
  requiredRefs = ['headerDrawerContainer', 'headerMenu', 'headerRowTop'];

  /**
   * Width of window when header drawer was hidden
   * @type {number | null}
   */
  #menuDrawerHiddenWidth = null;

  /**
   * An intersection observer for monitoring sticky header position
   * @type {IntersectionObserver | null}
   */
  #intersectionObserver = null;

  /**
   * Tracks the current sticky state to prevent unnecessary updates
   * @type {string | null}
   */
  #currentStickyState = null;

  /**
   * Whether the header has been scrolled offscreen, when sticky behavior is 'scroll-up'
   * @type {boolean}
   */
  #offscreen = false;

  /**
   * The last recorded scrollTop of the document, when sticky behavior is 'scroll-up
   * @type {number}
   */
  #lastScrollTop = 0;

  /**
   * A timeout to allow for hiding animation, when sticky behavior is 'scroll-up'
   * @type {number | null}
   */
  #timeout = null;

  /**
   * The duration to wait for hiding animation, when sticky behavior is 'scroll-up'
   * @constant {number}
   */
  #animationDelay = 100;

  /**
   * Debounce timeout for state changes to prevent flickering on mobile
   * @type {number | null}
   */
  #stateChangeTimeout = null;

  /**
   * Keeps the global `--header-height` custom property up to date,
   * which other theme components can then consume
   */
  #resizeObserver = new ResizeObserver(([entry]) => {
    if (!entry) return;

    const { height } = entry.target.getBoundingClientRect();
    document.body.style.setProperty('--header-height', `${height}px`);

    // Check if the menu drawer should be hidden in favor of the header menu
    if (this.#menuDrawerHiddenWidth && window.innerWidth > this.#menuDrawerHiddenWidth) {
      this.#updateMenuVisibility(false);
    }
  });

 /**
   * Checks if the header is at the top of the page and resets state accordingly
   * @returns {boolean} - Whether the header is at the top
   */
  #checkAndResetTopState() {
    const scrollTop = document.scrollingElement?.scrollTop ?? 0;
    const isAtTop = scrollTop === 0 || this.getBoundingClientRect().top >= 0;
    
    if (isAtTop) {
      this.dataset.scrollDirection = 'none';
      this.#offscreen = false;
      const stickyMode = this.getAttribute('sticky');
      if (stickyMode === 'always' || stickyMode === 'scroll-up') {
        if (this.#timeout) {
          clearTimeout(this.#timeout);
          this.#timeout = null;
        }
        if (this.#stateChangeTimeout) {
          clearTimeout(this.#stateChangeTimeout);
          this.#stateChangeTimeout = null;
        }
        this.#updateStickyState('inactive', true);
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            changeMetaThemeColor(this.refs.headerRowTop);
          });
        });
      }
      return true;
    }
    return false;
  }

  /**
   * Observes the header while scrolling the viewport to track when its actively sticky
   * @param {Boolean} alwaysSticky - Determines if we need to observe when the header is offscreen
   */
  #observeStickyPosition = (alwaysSticky = true) => {
    if (this.#intersectionObserver) return;

    const config = {
      threshold: alwaysSticky ? 1 : 0,
    };

    this.#intersectionObserver = new IntersectionObserver(([entry]) => {
      if (!entry) return;

      const { isIntersecting } = entry;

      if (this.#checkAndResetTopState()) {
        return;
      }

      if (alwaysSticky) {
        const newState = isIntersecting ? 'inactive' : 'active';
        this.#updateStickyState(newState); 
      } else {
        this.#offscreen = !isIntersecting || this.dataset.stickyState === 'active'; 
      }
    }, config);

    this.#intersectionObserver.observe(this);
  };

  /**
   * Handles the overflow minimum event from the header menu
   * @param {OverflowMinimumEvent} event
   */
  #handleOverflowMinimum = (event) => {
    this.#updateMenuVisibility(event.detail.minimumReached);
  };

  /**
   * Updates the visibility of the menu and drawer
   * @param {boolean} hideMenu - Whether to hide the menu and show the drawer
   */
  #updateMenuVisibility(hideMenu) {
    if (hideMenu) {
      this.refs.headerDrawerContainer.classList.remove('desktop:hidden');
      this.#menuDrawerHiddenWidth = window.innerWidth;
      this.refs.headerMenu.classList.add('hidden');
    } else {
      this.refs.headerDrawerContainer.classList.add('desktop:hidden');
      this.#menuDrawerHiddenWidth = null;
      this.refs.headerMenu.classList.remove('hidden');
    }
  }

  /**
   * Debounced method to update sticky state to prevent rapid changes
   * @param {string} newState - The new sticky state
   * @param {boolean} immediate - Whether to apply immediately without debounce
   */
  #updateStickyState(newState, immediate = false) {
    if (this.#currentStickyState === newState) return;

    if (this.#stateChangeTimeout) {
      clearTimeout(this.#stateChangeTimeout);
      this.#stateChangeTimeout = null;
    }

    const updateState = () => {
      if (this.#currentStickyState !== newState) {
        this.#currentStickyState = newState;
        this.dataset.stickyState = newState;
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            changeMetaThemeColor(this.refs.headerRowTop);
          });
        });
      }
    };

    if (immediate) {
      updateState();
    } else {
      // Add small delay to prevent rapid state changes on mobile
      this.#stateChangeTimeout = setTimeout(updateState, 50);
    }
  }

  #handleWindowScrollRaw = () => {
    const stickyMode = this.getAttribute('sticky');
     
    if (this.#checkAndResetTopState()) {
      const scrollTop = document.scrollingElement?.scrollTop ?? 0;
      this.#lastScrollTop = scrollTop;
      return;
    }

    const scrollTop = document.scrollingElement?.scrollTop ?? 0;
    
    if (scrollTop === 0) {
      if (this.#currentStickyState !== 'inactive') {
        this.#checkAndResetTopState();
      }
      this.#lastScrollTop = scrollTop;
      return;
    }
    
    if (!this.#offscreen && stickyMode !== 'always') return;

    const scrollDelta = Math.abs(scrollTop - this.#lastScrollTop);
    
    // Increase threshold for mobile devices to prevent flickering
    const isMobile = window.innerWidth <= 749;
    const threshold = isMobile ? 8 : 2;
    
    // Ignore small scroll changes to prevent jittery behavior
    if (scrollDelta < threshold) return;
    
    const isScrollingUp = scrollTop < this.#lastScrollTop;
    
    if (this.#timeout) {
      clearTimeout(this.#timeout);
      this.#timeout = null;
    }

    if (stickyMode === 'always') {
      if (isScrollingUp) {
        this.dataset.scrollDirection = 'up';
      } else {
        this.dataset.scrollDirection = 'down';
      }

      this.#lastScrollTop = scrollTop;
      return;
    }

    if (isScrollingUp) {
      this.removeAttribute('data-animating');
      // show sticky header when scrolling up
      this.#updateStickyState('active');
      this.dataset.scrollDirection = 'up';
    } else if (this.dataset.stickyState === 'active') {
      this.dataset.scrollDirection = 'none';
      // delay transitioning to idle hidden state for hiding animation
      this.setAttribute('data-animating', '');

      this.#timeout = setTimeout(() => {
        if (this.dataset.stickyState === 'active') {
          this.#updateStickyState('idle', true);
          this.removeAttribute('data-animating');
        }
      }, this.#animationDelay);
    } else {
      this.dataset.scrollDirection = 'none';
      this.#updateStickyState('idle');
    }

    this.#lastScrollTop = scrollTop;
  };

  // Throttle the scroll handler to improve performance
  #handleWindowScroll = throttle(this.#handleWindowScrollRaw, 16); // ~60fps

  connectedCallback() {
    super.connectedCallback();
    this.#resizeObserver.observe(this);
    this.addEventListener('overflowMinimum', this.#handleOverflowMinimum);

    const stickyMode = this.getAttribute('sticky');
    if (stickyMode) {
      this.#observeStickyPosition(stickyMode === 'always');

      if (stickyMode === 'scroll-up' || stickyMode === 'always') {
        document.addEventListener('scroll', this.#handleWindowScroll);
        const checkTopPosition = () => {
          this.#checkAndResetTopState();
        };
        
        requestAnimationFrame(checkTopPosition);
        
        setTimeout(checkTopPosition, 100);
      }
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
    this.#intersectionObserver?.disconnect();
    this.removeEventListener('overflowMinimum', this.#handleOverflowMinimum);
    document.removeEventListener('scroll', this.#handleWindowScroll);
    
    // Clean up timeouts
    if (this.#timeout) {
      clearTimeout(this.#timeout);
      this.#timeout = null;
    }
    if (this.#stateChangeTimeout) {
      clearTimeout(this.#stateChangeTimeout);
      this.#stateChangeTimeout = null;
    }
    
    document.body.style.setProperty('--header-height', '0px');
  }
}

if (!customElements.get('header-component')) {
  customElements.define('header-component', HeaderComponent);
}

onDocumentLoaded(() => {
  const header = document.querySelector('#header-component');
  const headerGroup = document.querySelector('#header-group');

  // Update header group height on resize of any child
  if (headerGroup) {
    const resizeObserver = new ResizeObserver(() => calculateHeaderGroupHeight(header, headerGroup));

    // Observe all children of the header group
    const children = headerGroup.children;
    for (let i = 0; i < children.length; i++) {
      const element = children[i];
      if (element === header || !(element instanceof HTMLElement)) continue;
      resizeObserver.observe(element);
    }

    // Also observe the header group itself for child changes
    const mutationObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'childList') {
          // Re-observe all children when the list changes
          const children = headerGroup.children;
          for (let i = 0; i < children.length; i++) {
            const element = children[i];
            if (element === header || !(element instanceof HTMLElement)) continue;
            resizeObserver.observe(element);
          }
        }
      }
    });

    mutationObserver.observe(headerGroup, { childList: true });
  }
});
